import { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { SxProps } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design";
import { BookingStatus } from "src/business/booking";
import {
  colorActionDanger,
  colorBackgroundInfo,
  colorBorder,
  colorBorderSuccessEmphasis,
  colorSurface,
  colorSurfaceDangerHover,
  colorTextError,
  colorBorder as colorBorderToken,
  colorTextMuted,
  colorTextPrimary,
  iconBgSuccess,
  iconColorSuccess,
  radiusCard,
  radiusInput,
} from "src/design/tokens";
import {
  BookingSegment,
  SLOTS_PER_DAY,
  formatDuration,
  isRowOpen,
  OpenWindow,
  slotLabelForRow,
} from "../dayGridHelpers";

interface DetailRowProps {
  label: string;
  value: string;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
      borderBottom: `1px solid ${colorBorder}`,
    }}
  >
    <Typography sx={{ fontSize: 13, color: colorTextMuted }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: colorTextPrimary,
        textAlign: "right",
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface NoticeProps {
  children: string;
}

const Notice = ({ children }: NoticeProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      mt: 1.5,
      p: 1.25,
      backgroundColor: colorBackgroundInfo,
      borderRadius: radiusCard,
    }}
  >
    <InfoOutlinedIcon sx={{ fontSize: 16, color: colorTextMuted, mt: "1px" }} />
    <Typography sx={{ fontSize: 12.5, color: colorTextMuted }}>
      {children}
    </Typography>
  </Box>
);

interface BookingInspectorProps {
  segment: BookingSegment | null;
  selectedDate: Dayjs;
  bookingDuration: number;
  openWindows: OpenWindow[];
  isCancelling: boolean;
  isConfirming: boolean;
  canUpdate: boolean;
  onCancelBooking: (segment: BookingSegment) => void;
  onConfirmBooking: (segment: BookingSegment) => void;
  /** Move the booking to another calendar day, keeping its time and table. */
  onRescheduleDate: (segment: BookingSegment, date: Dayjs) => void;
  onClose: () => void;
  sx?: SxProps;
}

/**
 * Details for one booking. A booking crossing midnight is a single entity here
 * however it was opened — from its head or its tail — so cancelling from either
 * side removes the whole thing.
 */
export const BookingInspector = ({
  segment,
  selectedDate,
  bookingDuration,
  openWindows,
  isCancelling,
  isConfirming,
  canUpdate,
  onCancelBooking,
  onConfirmBooking,
  onRescheduleDate,
  onClose,
  sx,
}: BookingInspectorProps) => {
  const { t } = useTranslation();
  // Cancelling is destructive and the button sits next to Close, so it takes a
  // second, deliberate click.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  if (!segment) return null;

  const { booking, absStartRow, totalRows } = segment;
  const absEndRow = absStartRow + totalRows;

  const startsAnotherDay = absStartRow < 0;
  const endsAnotherDay = absEndRow > SLOTS_PER_DAY;

  // Rows run relative to the selected day, so whole days of offset give the
  // calendar date each end falls on — a booking crossing midnight names both.
  const stamp = (row: number): string =>
    `${selectedDate
      .add(Math.floor(row / SLOTS_PER_DAY), "day")
      .format("DD/MM")} ${slotLabelForRow(row)}`;

  // Only rows on the visible day can be checked against today's opening hours.
  const visibleRows = Array.from(
    { length: segment.endRow - segment.startRow },
    (_, offset) => segment.startRow + offset,
  );
  const isOutsideOpening = visibleRows.every((row) => !isRowOpen(openWindows, row));

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: radiusCard, ...sx } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: colorTextPrimary }}>
        {t("tableSchedule.inspector.title")}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <DetailRow
          label={t("tableSchedule.inspector.guest")}
          value={booking.userName}
        />
        <DetailRow
          label={t("tableSchedule.inspector.seats")}
          value={String(booking.numberOfSeats)}
        />
        <DetailRow
          label={t("tableSchedule.inspector.table")}
          value={t("tableSchedule.tableLabelLong", { number: segment.table })}
        />
        <DetailRow
          label={t("tableSchedule.inspector.starts")}
          value={stamp(absStartRow)}
        />
        <DetailRow
          label={t("tableSchedule.inspector.ends")}
          value={stamp(absEndRow)}
        />
        <DetailRow
          label={t("tableSchedule.inspector.duration")}
          value={formatDuration(bookingDuration)}
        />
        <DetailRow
          label={t("tableSchedule.inspector.status")}
          value={t(`tableSchedule.status.${booking.bookingStatus}`)}
        />
        {/* An anonymous booking was entered by staff on the floor plan; anything
            else came from the guest through the public booking flow. */}
        <DetailRow
          label={t("tableSchedule.inspector.bookedBy")}
          value={
            booking.isAnonymous
              ? t("tableSchedule.inspector.bookedByStaff")
              : t("tableSchedule.inspector.bookedByGuest")
          }
        />
        {booking.dealContent && (
          <DetailRow
            label={t("tableSchedule.inspector.deal")}
            value={booking.dealContent}
          />
        )}
        {booking.eventContent && (
          <DetailRow
            label={t("tableSchedule.inspector.event")}
            value={booking.eventContent}
          />
        )}
        {booking.phone && (
          <DetailRow
            label={t("tableSchedule.inspector.phone")}
            value={booking.phone}
          />
        )}
        {booking.email && (
          <DetailRow
            label={t("tableSchedule.inspector.email")}
            value={booking.email}
          />
        )}
        {booking.notes && (
          <DetailRow
            label={t("tableSchedule.inspector.notes")}
            value={booking.notes}
          />
        )}

        {canUpdate && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{ fontSize: 12.5, color: colorTextMuted, mb: 0.75 }}
            >
              {t("tableSchedule.inspector.moveToDate")}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                // The booking keeps its time and table; only the day changes.
                value={selectedDate.add(
                  Math.floor(absStartRow / SLOTS_PER_DAY),
                  "day",
                )}
                // Typing into the field emits a value per keystroke, so partial
                // dates arrive as invalid Dayjs objects — only move on a real one.
                onChange={(value) =>
                  value?.isValid() && onRescheduleDate(segment, value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: radiusInput },
                      "& fieldset": { borderColor: colorBorderToken },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
        )}

        {(startsAnotherDay || endsAnotherDay) && (
          <Notice>{t("tableSchedule.inspector.spansMidnight")}</Notice>
        )}
        {isOutsideOpening && (
          <Notice>{t("tableSchedule.inspector.outsideOpening")}</Notice>
        )}
        {confirmingCancel && (
          <Notice>{t("tableSchedule.inspector.cancelConfirm")}</Notice>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {canUpdate && booking.bookingStatus === BookingStatus.PENDING && (
          <CustomButton
            variant="outlined"
            onClick={() => onConfirmBooking(segment)}
            disabled={isConfirming}
            sx={{
              color: iconColorSuccess,
              borderColor: colorBorderSuccessEmphasis,
              ":hover": {
                backgroundColor: iconBgSuccess,
                borderColor: colorBorderSuccessEmphasis,
              },
            }}
          >
            {t("tableSchedule.inspector.confirmBooking")}
          </CustomButton>
        )}
        <CustomButton
          variant="outlined"
          onClick={() =>
            confirmingCancel
              ? onCancelBooking(segment)
              : setConfirmingCancel(true)
          }
          disabled={isCancelling}
          sx={{
            color: colorTextError,
            backgroundColor: colorSurface,
            borderColor: colorActionDanger,
            ":hover": {
              backgroundColor: colorSurfaceDangerHover,
              borderColor: colorActionDanger,
            },
          }}
        >
          {confirmingCancel
            ? t("tableSchedule.inspector.confirmCancel")
            : t("tableSchedule.inspector.cancelBooking")}
        </CustomButton>
        <CustomButton color="black" variant="contained" onClick={onClose}>
          {t("tableSchedule.inspector.close")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
